import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  BarChart3,
  Settings,
  ChevronLeft,
  Leaf,
  BookOpen,
  Rocket,
  X,
  Target,
  Tag,
  Shield
} from 'lucide-react';
import { PageId } from '../contexts/PermissionsContext';

type UserRole = 'admin' | 'marketing' | 'new' | 'owner' | 'sales' | 'user' | null;

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  userRole?: UserRole;
  isMobile?: boolean;
  allowedPages?: PageId[];
}

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  pageId: PageId; // Maps to the page ID in permissions
}

// All possible nav items - visibility is controlled by allowedPages prop
const navItems: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', pageId: 'dashboard' },
  { to: '/studios', icon: Building2, label: 'Studios', pageId: 'studios' },
  { to: '/users', icon: Users, label: 'Users', pageId: 'users' },
  { to: '/subscriptions', icon: CreditCard, label: 'Subscriptions', pageId: 'subscriptions' },
  { to: '/discounts', icon: Tag, label: 'Discounts', pageId: 'discounts' },
  { to: '/leads', icon: Target, label: 'Leads', pageId: 'leads' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', pageId: 'analytics' },
  { to: '/guide', icon: BookOpen, label: 'Admin Guide', pageId: 'guide' },
  { to: '/playbook', icon: Rocket, label: 'Growth Playbook', pageId: 'playbook' },
  { to: '/roles', icon: Shield, label: 'Roles', pageId: 'roles' },
  { to: '/settings', icon: Settings, label: 'Settings', pageId: 'settings' },
];

export default function Sidebar({ isOpen, onToggle, userRole: _userRole, isMobile = false, allowedPages = [] }: SidebarProps) {
  // Filter nav items based on allowed pages from database
  const visibleNavItems = navItems.filter(item =>
    allowedPages.includes(item.pageId)
  );

  // Mobile sidebar - slide-in drawer with backdrop
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onToggle}
          />
        )}

        {/* Drawer */}
        <aside
          className={`fixed left-0 top-0 h-full w-72 bg-white dark:bg-stone-900 border-r border-stone-200 dark:border-stone-800 z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-stone-200 dark:border-stone-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sage rounded-xl flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-stone-800 dark:text-stone-100">SereneTouch</p>
                <p className="text-xs text-stone-500">Admin Portal</p>
              </div>
            </div>
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onToggle}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-sage/10 text-sage font-medium'
                      : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800'
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Version */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="p-4 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
              <p className="text-xs text-stone-500 dark:text-stone-400">v{__APP_VERSION__}</p>
            </div>
          </div>
        </aside>
      </>
    );
  }

  // Desktop sidebar - collapsible
  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-white dark:bg-stone-900 border-r border-stone-200 dark:border-stone-800 transition-all duration-300 z-40 hidden lg:block ${
        isOpen ? 'w-64' : 'w-20'
      }`}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-stone-200 dark:border-stone-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sage rounded-xl flex items-center justify-center">
            <Leaf className="w-6 h-6 text-white" />
          </div>
          {isOpen && (
            <div className="animate-fade-in">
              <p className="font-bold text-stone-800 dark:text-stone-100">SereneTouch</p>
              <p className="text-xs text-stone-500">Admin Portal</p>
            </div>
          )}
        </div>
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400 transition-colors"
        >
          <ChevronLeft className={`w-5 h-5 transition-transform ${!isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-2">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive
                  ? 'bg-sage/10 text-sage font-medium'
                  : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800'
              }`
            }
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {isOpen && <span className="animate-fade-in">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Version */}
      {isOpen && (
        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-4 bg-stone-50 dark:bg-stone-800/50 rounded-xl">
            <p className="text-xs text-stone-500 dark:text-stone-400">v{__APP_VERSION__}</p>
          </div>
        </div>
      )}
    </aside>
  );
}
